import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  Type,
} from '@angular/core';
import Sortable, { SortableEvent } from 'sortablejs';

import { RecordListCardComponent } from '../../record-list/record-list.types';

export interface BusinessDocumentKanbanCard {
  id: string;
  record: Record<string, unknown>;
  disabled?: boolean;
}

export interface BusinessDocumentKanbanColumn {
  id: string;
  title: string;
  cards: BusinessDocumentKanbanCard[];
  accent?: string;
  summary?: string;
  isInitial?: boolean;
  isTerminal?: boolean;
  transitions?: BusinessDocumentKanbanTransition[];
  defaultNextColumnId?: string | null;
}

export interface BusinessDocumentKanbanTransition {
  toColumnId: string;
  isDefault?: boolean;
}

export interface BusinessDocumentKanbanMove {
  cardId: string;
  fromColumnId: string;
  toColumnId: string;
}

interface BusinessDocumentKanbanLevel {
  id: string;
  columns: BusinessDocumentKanbanColumn[];
  terminal: boolean;
}

interface BusinessDocumentKanbanConnector {
  id: string;
  path: string;
  isDefault: boolean;
}

@Component({
  selector: 'app-business-document-kanban',
  standalone: false,
  templateUrl: './business-document-kanban.html',
  styleUrl: './business-document-kanban.scss',
})
export class BusinessDocumentKanban implements AfterViewInit, OnChanges, OnDestroy {
  @Input() columns: BusinessDocumentKanbanColumn[] = [];
  @Input() draggable = true;
  @Input() emptyMessage = 'No records in this pipeline.';
  @Input() viewportOffset = '18rem';
  @Input({ required: true }) cardComponent: Type<RecordListCardComponent> | null = null;
  @Input() cardContext: Readonly<Record<string, unknown>> = {};

  @Output() cardMoved = new EventEmitter<BusinessDocumentKanbanMove>();
  @Output() cardOpened = new EventEmitter<string>();

  @ViewChildren('columnList') private columnLists!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('stageBoard') private stageBoards!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('kanbanCanvas') private kanbanCanvas?: ElementRef<HTMLDivElement>;

  levels: BusinessDocumentKanbanLevel[] = [];
  connectors: BusinessDocumentKanbanConnector[] = [];
  connectorWidth = 0;
  connectorHeight = 0;

  private sortables: Sortable[] = [];
  private viewReady = false;
  private resizeObserver?: ResizeObserver;
  private layoutFrame?: number;

  constructor(private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.columnLists.changes.subscribe(() => {
      this.initializeSortables();
      this.observeCanvas();
      this.scheduleConnectorLayout();
    });
    this.stageBoards.changes.subscribe(() => this.scheduleConnectorLayout());
    this.initializeSortables();
    this.observeCanvas();
    this.scheduleConnectorLayout();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.levels = this.buildLevels(this.columns);
    if (this.viewReady) {
      queueMicrotask(() => {
        this.initializeSortables();
        this.observeCanvas();
        this.scheduleConnectorLayout();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroySortables();
    this.resizeObserver?.disconnect();
    if (this.layoutFrame !== undefined) cancelAnimationFrame(this.layoutFrame);
  }

  openCard(card: BusinessDocumentKanbanCard): void {
    this.cardOpened.emit(card.id);
  }

  cardInputs(card: BusinessDocumentKanbanCard): Record<string, unknown> {
    return { item: card.record, context: this.cardContext };
  }

  trackColumn(_index: number, column: BusinessDocumentKanbanColumn): string {
    return column.id;
  }

  trackLevel(_index: number, level: BusinessDocumentKanbanLevel): string {
    return level.id;
  }

  trackConnector(_index: number, connector: BusinessDocumentKanbanConnector): string {
    return connector.id;
  }

  trackCard(_index: number, card: BusinessDocumentKanbanCard): string {
    return card.id;
  }

  accentClass(column: BusinessDocumentKanbanColumn): string {
    return column.accent ? `kanban-accent-${this.safeClass(column.accent)}` : '';
  }

  isMoveAllowed(fromColumnId: string, toColumnId: string): boolean {
    if (!fromColumnId || !toColumnId) return false;
    if (fromColumnId === toColumnId) return true;
    return this.columns
      .find((column) => column.id === fromColumnId)
      ?.transitions?.some((transition) => transition.toColumnId === toColumnId) ?? false;
  }

  get canvasMinWidth(): string {
    return `max(100%, ${Math.max(this.levels.length, 1) * 20.125}rem)`;
  }

  private initializeSortables(): void {
    this.destroySortables();
    if (!this.viewReady || !this.draggable) return;

    this.zone.runOutsideAngular(() => {
      this.sortables = this.columnLists.map(({ nativeElement }) => Sortable.create(nativeElement, {
        group: 'business-document-pipeline',
        animation: 180,
        draggable: '.kanban-item:not(.is-disabled)',
        ghostClass: 'kanban-card-ghost',
        chosenClass: 'kanban-card-chosen',
        dragClass: 'kanban-card-dragging',
        emptyInsertThreshold: 36,
        onMove: (event) => this.isMoveAllowed(
          (event.from as HTMLElement).dataset['columnId'] ?? '',
          (event.to as HTMLElement).dataset['columnId'] ?? '',
        ),
        onStart: (event: SortableEvent) => this.showDropTargets(
          (event.from as HTMLElement).dataset['columnId'] ?? '',
        ),
        onEnd: (event: SortableEvent) => this.handleMove(event),
      }));
    });
  }

  private handleMove(event: SortableEvent): void {
    const cardId = (event.item as HTMLElement).dataset['cardId'] ?? '';
    const fromColumnId = (event.from as HTMLElement).dataset['columnId'] ?? '';
    const toColumnId = (event.to as HTMLElement).dataset['columnId'] ?? '';
    this.clearDropTargets();
    if (
      cardId
      && fromColumnId
      && toColumnId
      && fromColumnId !== toColumnId
      && this.isMoveAllowed(fromColumnId, toColumnId)
    ) {
      this.zone.run(() => this.cardMoved.emit({ cardId, fromColumnId, toColumnId }));
    }
  }

  private destroySortables(): void {
    this.sortables.forEach((sortable) => sortable.destroy());
    this.sortables = [];
  }

  private buildLevels(columns: BusinessDocumentKanbanColumn[]): BusinessDocumentKanbanLevel[] {
    if (columns.length === 0) return [];

    const byId = new Map(columns.map((column) => [column.id, column]));
    const indexById = new Map(columns.map((column, index) => [column.id, index]));
    const nonTerminal = columns.filter((column) => !column.isTerminal);
    const initial = nonTerminal.find((column) => column.isInitial) ?? nonTerminal[0];
    const depth = new Map<string, number>();
    const queue: BusinessDocumentKanbanColumn[] = [];
    if (initial) {
      depth.set(initial.id, 0);
      queue.push(initial);
    }

    while (queue.length > 0) {
      const source = queue.shift()!;
      const sourceDepth = depth.get(source.id) ?? 0;
      const targets = this.sortedTransitions(source)
        .map((transition) => byId.get(transition.toColumnId))
        .filter((target): target is BusinessDocumentKanbanColumn => !!target && !target.isTerminal);
      for (const target of targets) {
        const nextDepth = sourceDepth + 1;
        const currentDepth = depth.get(target.id);
        if (currentDepth === undefined || nextDepth < currentDepth) {
          depth.set(target.id, nextDepth);
          queue.push(target);
        }
      }
    }

    let lastDepth = Math.max(0, ...depth.values());
    for (const column of nonTerminal) {
      if (!depth.has(column.id)) depth.set(column.id, ++lastDepth);
    }

    const incomingDefault = new Set<string>();
    for (const column of columns) {
      const preferred = column.defaultNextColumnId
        ?? column.transitions?.find((transition) => transition.isDefault)?.toColumnId;
      if (preferred) incomingDefault.add(preferred);
    }

    const grouped = new Map<number, BusinessDocumentKanbanColumn[]>();
    const sortColumns = (
      left: BusinessDocumentKanbanColumn,
      right: BusinessDocumentKanbanColumn,
    ): number => {
      const defaultOrder = Number(incomingDefault.has(right.id)) - Number(incomingDefault.has(left.id));
      return defaultOrder || (indexById.get(left.id) ?? 0) - (indexById.get(right.id) ?? 0);
    };
    for (const column of nonTerminal) {
      const level = depth.get(column.id) ?? 0;
      grouped.set(level, [...(grouped.get(level) ?? []), column]);
    }
    const levels = [...grouped.entries()]
      .sort(([left], [right]) => left - right)
      .map(([level, levelColumns]) => ({
        id: `level-${level}`,
        terminal: false,
        columns: levelColumns.sort(sortColumns),
      }));
    const terminalColumns = columns.filter((column) => column.isTerminal).sort(sortColumns);
    if (terminalColumns.length > 0) {
      levels.push({ id: 'terminal', terminal: true, columns: terminalColumns });
    }
    return levels;
  }

  private sortedTransitions(column: BusinessDocumentKanbanColumn): BusinessDocumentKanbanTransition[] {
    const preferred = column.defaultNextColumnId;
    return [...(column.transitions ?? [])].sort((left, right) => {
      const leftDefault = left.isDefault || left.toColumnId === preferred;
      const rightDefault = right.isDefault || right.toColumnId === preferred;
      return Number(rightDefault) - Number(leftDefault);
    });
  }

  private showDropTargets(sourceColumnId: string): void {
    this.stageBoards.forEach(({ nativeElement }) => {
      const targetId = nativeElement.dataset['columnId'] ?? '';
      nativeElement.classList.toggle('is-drop-allowed', this.isMoveAllowed(sourceColumnId, targetId));
      nativeElement.classList.toggle('is-drop-blocked', !this.isMoveAllowed(sourceColumnId, targetId));
    });
  }

  private clearDropTargets(): void {
    this.stageBoards.forEach(({ nativeElement }) => {
      nativeElement.classList.remove('is-drop-allowed', 'is-drop-blocked');
    });
  }

  private observeCanvas(): void {
    this.resizeObserver?.disconnect();
    const canvas = this.kanbanCanvas?.nativeElement;
    if (!canvas || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => this.scheduleConnectorLayout());
    this.resizeObserver.observe(canvas);
    this.stageBoards.forEach(({ nativeElement }) => this.resizeObserver?.observe(nativeElement));
  }

  private scheduleConnectorLayout(): void {
    if (!this.viewReady || typeof requestAnimationFrame === 'undefined') return;
    if (this.layoutFrame !== undefined) cancelAnimationFrame(this.layoutFrame);
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = undefined;
      this.zone.run(() => this.updateConnectors());
    });
  }

  private updateConnectors(): void {
    const canvas = this.kanbanCanvas?.nativeElement;
    if (!canvas) return;
    const canvasRect = canvas.getBoundingClientRect();
    const boards = new Map(
      this.stageBoards.map(({ nativeElement }) => [nativeElement.dataset['columnId'] ?? '', nativeElement]),
    );
    this.connectorWidth = Math.max(canvas.scrollWidth, canvas.clientWidth);
    this.connectorHeight = Math.max(canvas.scrollHeight, canvas.clientHeight);
    const connectors: BusinessDocumentKanbanConnector[] = [];
    for (const source of this.columns) {
      const sourceElement = boards.get(source.id);
      if (!sourceElement) continue;
      const sourceRect = sourceElement.getBoundingClientRect();
      for (const transition of source.transitions ?? []) {
        const targetElement = boards.get(transition.toColumnId);
        if (!targetElement) continue;
        const targetRect = targetElement.getBoundingClientRect();
        const startX = sourceRect.right - canvasRect.left;
        const startY = sourceRect.top - canvasRect.top + sourceRect.height / 2;
        const endX = targetRect.left - canvasRect.left;
        const endY = targetRect.top - canvasRect.top + targetRect.height / 2;
        const middleX = startX + (endX - startX) / 2;
        connectors.push({
          id: `${source.id}-${transition.toColumnId}`,
          isDefault: !!transition.isDefault || source.defaultNextColumnId === transition.toColumnId,
          path: `M ${startX} ${startY} H ${middleX} V ${endY} H ${endX}`,
        });
      }
    }
    this.connectors = connectors;
  }

  private safeClass(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9_-]/g, '-');
  }

}
